import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useBatchRevalidatedHeadSha, useUnifiedBatch, useUnifiedBatchActions} from '../stores/layout-selectors'
import {isCommittableSuggestion} from '../stores/slices/unified-batch'

type UnifiedComment = {
  id: number
  suggestionIndex?: number
}

/**
 * Rejected because the head moved after the batch was validated (`HEAD_OID_MISMATCH` 409). A named
 * class lets callers tell this apart from other commit failures (e.g. telemetry) without message matching.
 */
export class HeadOidMismatchError extends Error {
  constructor(status?: number) {
    super('The pull request has been updated since this batch was created. Please refresh the page and try again.', {
      cause: status,
    })
    this.name = 'HeadOidMismatchError'
  }
}

/**
 * Commits the Commit suggestions tab of the unified batch panel.
 *
 * Mirrors {@link useApplyBatchedSuggestionsMutation} (the legacy batch path):
 * the client only sends comment identifiers — the server extracts the suggestion
 * content from each comment — so there is no client-side `SuggestedChange[]`
 * hydration. It sources comments from the `unifiedBatch` slice (excluding
 * outdated and comment-only items) instead of the legacy `suggestionBatch` slice.
 *
 * `expectedHeadOid` is the head the batch was last *revalidated* against
 * (`revalidatedHeadSha`), not the live head — matching the server's contract for
 * the param. After a push the live head advances but `revalidatedHeadSha` does not
 * until Refresh, so the server sees the stale OID and returns a `HEAD_OID_MISMATCH`
 * 409 (surfaced inline in the Commit tab) on every route, making the server the
 * authority that blocks a stale commit. Refresh advances `revalidatedHeadSha`, so a
 * reconciled batch commits cleanly.
 */
export function useApplyUnifiedBatchSuggestionsMutation() {
  const apiUrl = usePageDataUrl(PageData.applySuggestedChanges)
  const headOid = useBatchRevalidatedHeadSha()
  const {items} = useUnifiedBatch()
  const {clearBatch, setSubmitting} = useUnifiedBatchActions()

  return useMutation({
    onMutate: () => {
      setSubmitting(true)
    },
    mutationFn: async ({message}: {message: string}) => {
      const comments: UnifiedComment[] = items.filter(isCommittableSuggestion).map(item => {
        const comment: UnifiedComment = {id: item.commentDatabaseId}
        if (item.suggestionIndex != null) {
          comment.suggestionIndex = item.suggestionIndex
        }
        return comment
      })

      if (comments.length === 0) {
        throw new Error('No valid suggestions found in batch')
      }

      const response = await reactFetchJSON(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {
          comments,
          message,
          expectedHeadOid: headOid,
        },
      })

      const json = await parseJSONWithBetterErrors(response)

      if (!response.ok && json?.code === 'HEAD_OID_MISMATCH') {
        throw new HeadOidMismatchError(response.status)
      }

      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: () => {
      clearBatch()
    },
    onSettled: () => {
      setSubmitting(false)
    },
  })
}
